import { createSlice } from "@reduxjs/toolkit";
import {
  addMembersItem,
  fetchMembersItems,
  fetchStripeAccount,
  loadMoreMembersItems,
  updateMembersItem,
} from "./MembersThunks";

const initialState = {
  items: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  hasMore: true,
  searchTerm: "",
};

const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {
    clearMembers: (state) => {
      state.items = [];
      state.currentPage = 1;
      state.hasMore = true;
      state.error = null;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    resetPagination: (state) => {
      state.currentPage = 1;
      state.hasMore = true;
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Members Items (initial load or search)
      .addCase(fetchMembersItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMembersItems.fulfilled, (state, action) => {
        state.loading = false;
        const { data, page, totalPages, total, hasNextPage, reset } =
          action.payload;
        if (reset) {
          // Reset/replace data for new search or initial load
          state.items = data || [];
          state.currentPage = page || 1;
        } else {
          // Append data for pagination
          state.items = [...state.items, ...(data || [])];
          state.currentPage = page || state.currentPage;
        }

        state.totalPages = totalPages || 0;
        state.totalItems = total || 0;
        state.hasMore =
          hasNextPage !== undefined
            ? hasNextPage
            : state.currentPage < state.totalPages;
        state.error = null;
      })
      .addCase(fetchMembersItems.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "Failed to fetch members";
      })

      // Load More Members (infinite scroll)
      .addCase(loadMoreMembersItems.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreMembersItems.fulfilled, (state, action) => {
        state.loadingMore = false;
        const { data, pagination } = action.payload;

        // Append new items to existing items
        state.items = [...state.items, ...(data || [])];
        state.currentPage = pagination?.currentPage || state.currentPage + 1;
        state.totalPages = pagination?.totalPages || state.totalPages;
        state.totalItems = pagination?.totalItems || state.totalItems;
        state.hasMore =
          pagination?.hasMore !== undefined
            ? pagination.hasMore
            : state.currentPage < state.totalPages;
        state.error = null;
      })
      .addCase(loadMoreMembersItems.rejected, (state, action) => {
        state.loadingMore = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to load more members";
      })
      // Fetch account
      .addCase(fetchStripeAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStripeAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.account = action.payload;
      })
      .addCase(fetchStripeAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      }) // Add Member Item
      .addCase(addMembersItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMembersItem.fulfilled, (state, action) => {
        state.loading = false;
        // Add new member to the beginning of the list
        if (action.payload?.data) {
          state.items = [action.payload.data, ...state.items];
          state.totalItems += 1;
        }
        state.error = null;
      })
      .addCase(addMembersItem.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "Failed to add member";
      })

      // Update Member Item
      .addCase(updateMembersItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMembersItem.fulfilled, (state, action) => {
        state.loading = false;
        // Update the member in the list
        if (action.payload?.data) {
          const index = state.items.findIndex(
            (item) => item._id === action.payload.data._id
          );
          if (index !== -1) {
            state.items[index] = action.payload.data;
          }
        }
        state.error = null;
      })
      .addCase(updateMembersItem.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "Failed to update member";
      });
  },
});

export const { clearMembers, setSearchTerm, resetPagination } =
  membersSlice.actions;
export default membersSlice.reducer;
